import React, { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { getCurrencySymbol } from "../lib/currency";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { ScrollArea } from "../components/ui/scroll-area";
import { MultiSelect } from "../components/ui/multi-select";
import { DeleteConfirmModal } from "../components/DeleteConfirmModal";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Separator } from "../components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import {
  Plus, Edit2, Trash2, ChevronDown, Building2, X, Lock, Clock, Tag,
  ChevronLeft, ChevronRight, Filter, Search, Loader2
} from "lucide-react";

const EMPTY_FORM = {
  name: "", description: "", price: "",
  durationMonths: "1", durationDays: "0",
  status: "Active", notes: "", branchIds: [],
};

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 640px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}

export default function Packages() {
  const { organization, hasPermission } = useAuth();
  const currencySymbol = getCurrencySymbol(organization?.currency);
  const isDesktop = useIsDesktop();

  // ── Permission flags ─────────────────────────────────────────────
  const canView = hasPermission("view_packages");
  const canAdd = hasPermission("add_package");
  const canEdit = hasPermission("edit_package");
  const canDelete = hasPermission("delete_package");

  const [packages, setPackages] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteModal, setDeleteModal] = useState({ open: false, pkg: null });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [expandedRowId, setExpandedRowId] = useState(null);

  // Filtering states
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  // Staged filter states
  const [stagedSearch, setStagedSearch] = useState("");
  const [stagedFilterStatus, setStagedFilterStatus] = useState("all");

  // Server Pagination/Filtering states
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });

  const fetchBranches = async () => {
    try {
      const res = await api.get("/branches");
      setBranches(res.data.branches || res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const res = await api.get("/packages", {
        params: {
          name: search,
          status: filterStatus,
          page,
          limit,
        },
      });
      setPackages(res.data.packages || []);
      setTotal(res.data.total || 0);
      if (res.data.stats) {
        setStats(res.data.stats);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) {
      fetchBranches();
    }
  }, [canView]);

  useEffect(() => {
    if (canView) {
      fetchPackages();
    }
  }, [canView, page, limit, search, filterStatus]);

  const update = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const handleSave = async () => {
    if (!form.name || !form.price) { toast.error("Name and price required"); return; }
    const months = parseInt(form.durationMonths) || 0;
    const days = parseInt(form.durationDays) || 0;
    if (months === 0 && days === 0) { toast.error("Duration must be at least 1 day"); return; }
    setSaving(true);
    try {
      const data = { ...form, price: parseFloat(form.price), durationMonths: months, durationDays: days };
      if (editing) {
        await api.put(`/packages/${editing.id}`, data);
        toast.success("Package updated");
      } else {
        await api.post("/packages", data);
        toast.success("Package created");
      }
      setShowForm(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      fetchPackages();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const handleEdit = (pkg) => {
    setEditing(pkg);
    setForm({
      name: pkg.name,
      description: pkg.description || "",
      price: String(pkg.price),
      durationMonths: String(pkg.durationMonths ?? 1),
      durationDays: String(pkg.durationDays || 0),
      status: pkg.status || "Active",
      notes: pkg.notes || "",
      branchIds: Array.isArray(pkg.branchIds) ? pkg.branchIds : [],
    });
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!deleteModal.pkg) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/packages/${deleteModal.pkg.id}`);
      toast.success(`"${deleteModal.pkg.name}" has been removed`);
      setDeleteModal({ open: false, pkg: null });
      fetchPackages();
    } catch { toast.error("Failed to delete package"); }
    finally { setDeleteLoading(false); }
  };

  const toggleRow = (id) => setExpandedRowId((prev) => (prev === id ? null : id));

  // Server-side filtering variables
  const paginatedPackages = packages;

  const activeFilterCount = [
    search,
    filterStatus !== "all" ? filterStatus : "",
  ].filter(v => !!v).length;

  const hasActiveFilters = activeFilterCount > 0;

  const handleOpenFilters = () => {
    setStagedSearch(search);
    setStagedFilterStatus(filterStatus);
    setShowFilters(true);
  };

  const handleApplyFilters = () => {
    setSearch(stagedSearch);
    setFilterStatus(stagedFilterStatus);
    setPage(1);
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    setStagedSearch("");
    setStagedFilterStatus("all");
    setSearch("");
    setFilterStatus("all");
    setPage(1);
    setShowFilters(false);
  };

  const renderPackageDetails = (pkg) => {
    const branchNames = (pkg.branchIds || []).map(id => branches.find(b => b.id === id)?.name).filter(Boolean);
    const duration = [
      pkg.durationMonths > 0 ? `${pkg.durationMonths} month${pkg.durationMonths > 1 ? "s" : ""}` : "",
      pkg.durationDays > 0 ? `${pkg.durationDays} day${pkg.durationDays > 1 ? "s" : ""}` : "",
    ].filter(Boolean).join(" ") || "—";

    return (
      <div className="flex flex-col">
        {/* Action Bar */}
        <div className="flex items-center justify-between gap-2 p-2 border-b border-border bg-secondary/20">
          <div className="text-xs text-muted-foreground font-mono hidden md:block">
            Package ID: #{pkg.id}
          </div>
          <div className="flex items-center gap-2">
            {canEdit && (
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide"
                onClick={(e) => { e.stopPropagation(); handleEdit(pkg); }}
                data-testid={`edit-pkg-${pkg.id}`}>
                <Edit2 className="h-3.5 w-3.5" /> <span className="hidden md:block">Edit Package</span>
              </Button>
            )}
            {canDelete && (
              <Button variant="ghost" size="sm" className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide text-red-500 hover:text-red-600 hover:bg-red-500/10 ml-auto"
                onClick={(e) => { e.stopPropagation(); setDeleteModal({ open: true, pkg }); }}
                data-testid={`delete-pkg-${pkg.id}`}>
                <Trash2 className="h-3.5 w-3.5" /> <span className="hidden md:block">Delete Package</span>
              </Button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-2">
          {/* Left Column: Duration, Price, Branches */}
          <div className="lg:col-span-4 space-y-6 p-3">
            <div>
              <p className="text-xs font-mono uppercase tracking-tight text-muted-foreground mb-2">Duration</p>
              <div className="flex items-center gap-1.5 text-sm font-semibold">
                <Clock className="h-4 w-4 text-muted-foreground" />
                {duration}
              </div>
            </div>
            <div>
              <p className="text-xs font-mono uppercase tracking-tight text-muted-foreground mb-2">Price</p>
              <div className="font-mono text-sm font-semibold">
                {currencySymbol}{pkg.price?.toLocaleString()}
              </div>
            </div>
            <div>
              <p className="text-xs font-mono uppercase tracking-tight text-muted-foreground mb-2">Branches</p>
              <div className="flex flex-wrap gap-1.5">
                {branchNames.length > 0 ? (
                  branchNames.map(name => (
                    <Badge key={name} variant="secondary" className="text-[10px] font-mono px-2 py-0.5 bg-blue-500/15 text-blue-500 border border-blue-500/20">
                      <Building2 className="h-3 w-3 mr-1" /> {name}
                    </Badge>
                  ))
                ) : (
                  <Badge variant="secondary" className="text-[10px] font-mono px-2 py-0.5">
                    All branches
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Description & Notes */}
          <div className="lg:col-span-8 space-y-6 lg:border-l lg:border-border p-3 border-t md:border-t-0">
            <div>
              <p className="text-xs font-mono uppercase tracking-tight text-muted-foreground mb-2">Description</p>
              {pkg.description ? (
                <p className="text-sm whitespace-pre-wrap">{pkg.description}</p>
              ) : (
                <p className="text-sm text-muted-foreground italic">No description provided</p>
              )}
            </div>
            {pkg.notes && (
              <div>
                <p className="text-xs font-mono uppercase tracking-tight text-muted-foreground mb-2">Notes</p>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{pkg.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Access denied ─────────────────────────────────────────────
  if (!canView) {
    return (
      <div className="space-y-6" data-testid="packages-page">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight">Packages</h1>
        </div>
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center">
              <Lock className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="text-center">
              <p className="font-semibold text-base">Access Restricted</p>
              <p className="text-sm text-muted-foreground mt-1">You don't have permission to view Packages.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const showActionsCol = canEdit || canDelete;


  const statCards = [
    { label: "TOTAL PACKAGES", value: stats.total, icon: Tag },
    { label: "ACTIVE", value: stats.active, icon: Tag, color: "text-green-500" },
    { label: "INACTIVE", value: stats.inactive, icon: Tag, color: "text-red-500" },
  ];

  return (
    <div className="flex flex-col flex-1 min-h-0 space-y-2 mt-2 md:space-y-3" data-testid="packages-page">

      {/* ── Header ── */}
      <div className="flex sm:flex-row sm:items-center justify-between gap-2 md:gap-4 ">
        <div>
          <h1 className="text-xl md:text-4xl font-black font-heading tracking-tight uppercase">Packages</h1>
        </div>
        <div className="flex gap-2">
          <Button variant={activeFilterCount > 0 ? "secondary" : "outline"} size="sm"
            onClick={handleOpenFilters} className="font-heading uppercase tracking-wide text-xs" data-testid="toggle-filters-btn">
            <Filter className="h-4 w-4" /> <span className="hidden sm:block">Filters</span>
            {activeFilterCount > 0 && (
              <Badge className="bg-primary text-primary-foreground h-4 min-w-4 px-1 rounded-full text-[10px]">{activeFilterCount}</Badge>
            )}
          </Button>
          {canAdd && (
            <Button size="sm" onClick={() => { setEditing(null); setForm(EMPTY_FORM); setShowForm(true); }} className="font-heading uppercase tracking-wide text-xs" data-testid="add-package-btn">
              <Plus className="h-4 w-4" /><span className="">Add Package</span>
            </Button>
          )}
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="flex overflow-x-auto gap-2 pb-1 -mx-4 px-4 md:grid md:grid-cols-3 md:gap-3 md:mx-0 md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {statCards.map((s, i) => (
          <Card key={i} className="bg-card border-border group hover:border-primary/40 transition-all min-w-[105px] md:min-w-0 flex-1 shrink-0">
            <CardContent className="p-2.5 sm:p-4">
              <div className="flex items-center gap-1 sm:gap-2">
                <s.icon className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${s.color || "text-muted-foreground"} group-hover:text-primary transition-colors shrink-0`} />
                <p className="text-[8px] sm:text-[10px] text-muted-foreground font-mono uppercase tracking-wider sm:tracking-widest leading-tight truncate">{s.label}</p>
              </div>
              <p className={`text-base sm:text-2xl font-black font-heading mt-1 sm:mt-1.5 ${s.color || ""}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── Filter Sheet ── */}
      <Sheet open={showFilters} onOpenChange={setShowFilters}>
        <SheetContent
          side={isDesktop ? "right" : "bottom"}
          className={isDesktop
            ? "w-[400px] flex flex-col p-0 bg-card border-border"
            : "rounded-t-2xl flex flex-col p-0 max-h-[92vh] bg-card border-border"
          }
        >
          {/* Header */}
          <SheetHeader className="px-6 pt-5 pb-4 border-b border-border shrink-0">
            <div className="flex items-center justify-between">
              <SheetTitle className="font-heading uppercase tracking-wide text-base flex items-center gap-2">
                <Filter className="h-4 w-4" /> Filters
                {activeFilterCount > 0 && (
                  <Badge className="bg-primary text-primary-foreground text-[10px] px-1.5">{activeFilterCount} active</Badge>
                )}
              </SheetTitle>
              {activeFilterCount > 0 && (
                <Button variant="ghost" size="sm" onClick={handleClearFilters} className="text-muted-foreground text-xs h-7">
                  <X className="h-3.5 w-3.5 mr-1" />Clear all
                </Button>
              )}
            </div>
          </SheetHeader>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {/* Search */}
            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search packages..."
                  value={stagedSearch}
                  onChange={(e) => setStagedSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
                  className="pl-9"
                  data-testid="pkg-search"
                />
              </div>
            </div>

            <Separator />

            {/* Status */}
            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Status</Label>
              <Select value={stagedFilterStatus} onValueChange={setStagedFilterStatus}>
                <SelectTrigger data-testid="filter-status"><SelectValue placeholder="All Statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-border bg-card shrink-0">
            <Button className="w-full font-heading uppercase tracking-wide text-xs" onClick={handleApplyFilters}>
              <Search className="h-4 w-4 mr-2" />Apply Filters
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Table + Mobile Cards ── */}
      <Card className="bg-card border-x-0 md:border border-y border-border overflow-hidden flex flex-col flex-1 min-h-0  md:mx-0 rounded-lg">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground text-sm flex-1">
            <Loader2 className="animate-spin h-5 w-5 mr-2" /> Loading...
          </div>
        ) : packages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground flex-1">
            <Tag className="h-10 w-10 opacity-50" />
            <p className="text-sm">{hasActiveFilters ? "No packages match your filters." : "No packages yet"}</p>
            {!hasActiveFilters && canAdd && (
              <Button size="sm" className="mt-1" onClick={() => { setEditing(null); setForm(EMPTY_FORM); setShowForm(true); }}>
                <Plus className="h-4 w-4 mr-1" />Add your first package
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-auto flex-1">
              <Table>
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedPackages.map((pkg) => (
                    <React.Fragment key={pkg.id}>
                      <TableRow className="group cursor-pointer hover:bg-secondary/50" data-testid={`pkg-row-${pkg.id}`} onClick={() => toggleRow(pkg.id)}>
                        <TableCell className="w-10">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); toggleRow(pkg.id); }}>
                            <ChevronDown className={`h-4 w-4 transition-transform ${expandedRowId === pkg.id ? "rotate-180" : ""}`} />
                          </Button>
                        </TableCell>
                        <TableCell className="font-semibold text-sm">{pkg.name}</TableCell>
                        <TableCell className="font-mono text-sm">{currencySymbol}{pkg.price?.toLocaleString()}</TableCell>
                        <TableCell className="text-sm">
                          {pkg.durationMonths > 0 ? `${pkg.durationMonths}mo ` : ""}
                          {pkg.durationDays > 0 ? `${pkg.durationDays}d` : ""}
                          {!pkg.durationMonths && !pkg.durationDays ? "—" : ""}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${pkg.status === "Active" ? "bg-green-500/15 text-green-500 border border-green-500/20" : "bg-red-500/15 text-red-500 border border-red-500/20"
                            } border text-[10px] font-mono uppercase rounded-sm px-1.5 py-0`}>{pkg.status}</Badge>
                        </TableCell>
                      </TableRow>
                      {expandedRowId === pkg.id && (
                        <TableRow className="bg-secondary/30 hover:bg-secondary/30 border-b border-border">
                          <TableCell colSpan={5} className="p-0">
                            {renderPackageDetails(pkg)}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card List */}
            <div className="md:hidden overflow-x-hidden overflow-y-auto flex-1">
              {paginatedPackages.map((pkg, idx) => (
                <Collapsible
                  key={pkg.id}
                  open={expandedRowId === pkg.id}
                  onOpenChange={() => toggleRow(pkg.id)}
                  asChild
                >
                  <div data-testid={`pkg-card-${pkg.id}`}
                    className={` border-b border-border hover:bg-secondary/10 transition-colors`}>

                    <CollapsibleTrigger asChild>
                      <div className="flex items-center justify-between gap-2 cursor-pointer select-none px-2 py-2.5">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <div className="flex items-center justify-between flex-col">
                            <Button variant="ghost" size="icon" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground shrink-0">
                              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expandedRowId === pkg.id ? "rotate-180" : ""}`} />
                            </Button>
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-sm leading-tight truncate">{pkg.name}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="font-mono text-xs font-bold">{currencySymbol}{pkg.price?.toLocaleString()}</span>
                              <span className="text-[10px] text-muted-foreground font-mono">
                                {[
                                  pkg.durationMonths > 0 ? `${pkg.durationMonths}mo` : "",
                                  pkg.durationDays > 0 ? `${pkg.durationDays}d` : "",
                                ].filter(Boolean).join(" ") || "—"}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge className={`${pkg.status === "Active" ? "bg-green-500/15 text-green-500 border border-green-500/20" : "bg-red-500/15 text-red-500 border border-red-500/20"
                            } border text-[8px] font-mono uppercase rounded-sm px-1`}>{pkg.status}</Badge>
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className=" border-t border-border/50 animate-in fade-in slide-in-from-top-1 duration-200">
                        {renderPackageDetails(pkg)}
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              ))}
            </div>

            {/* Pagination */}
            <div className="flex flex-row items-center justify-between gap-4 p-4 border-t border-border bg-card shrink-0">
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="font-mono hidden sm:block">{total} total</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs">Show:</span>
                  <Select value={String(limit)} onValueChange={(v) => { setLimit(Number(v)); setPage(1); }}>
                    <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
                <span className="sm:text-sm text-xs text-muted-foreground px-2">Page {page} of {Math.ceil(total / limit) || 1}</span>
                <Button variant="outline" size="sm" disabled={page * limit >= total} onClick={() => setPage(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={showForm} onOpenChange={(o) => { if (!saving) { setShowForm(o); } }}>
        <DialogContent className="bg-card border-border rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit" : "Create"} Package</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-1">
            <div className="space-y-4 p-1">

              <div>
                <Label>Name *</Label>
                <Input value={form.name} onChange={(e) => update("name", e.target.value)} className="rounded-sm mt-1" data-testid="pkg-name" />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => update("description", e.target.value)} className="rounded-sm mt-1" />
              </div>

              <div>
                <Label>Price * ({currencySymbol})</Label>
                <Input type="number" value={form.price} onChange={(e) => update("price", e.target.value)} className="rounded-sm mt-1" data-testid="pkg-price" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Months (0–12)</Label>
                  <Select value={form.durationMonths} onValueChange={(v) => update("durationMonths", v)}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="pkg-months"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[...Array(13)].map((_, i) => (
                        <SelectItem key={i} value={String(i)}>{i}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Days (0–30)</Label>
                  <Select value={form.durationDays} onValueChange={(v) => update("durationDays", v)}>
                    <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[...Array(31)].map((_, i) => (
                        <SelectItem key={i} value={String(i)}>{i}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {branches.length > 0 && (
                <div className="mt-5">
                  <Label className="flex items-center gap-1.5 mb-2">
                    <Building2 className="h-3.5 w-3.5" /> Assign Branches
                    <span className="text-muted-foreground font-normal text-xs">(leave empty = all branches)</span>
                  </Label>
                  <MultiSelect
                    options={branches.map(b => ({ label: b.name, value: b.id }))}
                    selected={form.branchIds}
                    onChange={(vals) => update("branchIds", vals)}
                    placeholder="Select branches..."
                  />
                </div>
              )}

              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => update("status", v)}>
                  <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} className="rounded-sm mt-1" />
              </div>

              <Button className="w-full rounded-sm" onClick={handleSave} disabled={saving} data-testid="save-pkg-btn">
                {saving ? "Saving..." : (editing ? "Update" : "Create") + " Package"}
              </Button>

            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Modal */}
      <DeleteConfirmModal
        open={deleteModal.open}
        onOpenChange={(o) => setDeleteModal({ open: o, pkg: o ? deleteModal.pkg : null })}
        title="Delete Package"
        description="This package will be soft-deleted and hidden from new assignments. All existing memberships using this package will remain active until their expiry date."
        itemName={deleteModal.pkg?.name}
        onConfirm={handleDelete}
        loading={deleteLoading}
      />
    </div>
  );
}
